import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

// Swiper is loaded globally via script tag in scripts.liquid
// Define a web component for the carousel
class ProductCarousel extends HTMLElement {
  constructor() {
    super();
    this.productCarouselWrapper = this;
    this.swiperEl = this.querySelector('.swiper');
    this.nextArrow = this.querySelector('.swiper-button-next');
    this.prevArrow = this.querySelector('.swiper-button-prev');  
    this.sectionId = this.dataset.section_id || '';
    this.parentSection = this.closest('[slider-parent-wrapper]');
    this.isRecentlyViewed = this.dataset.recentlyViewed === 'true';
    this.isRecommendations = this.dataset.recommendations === 'true';
    this.currentProductId = this.dataset.currentProductId || null;
    this.showHalfSlides = this.dataset.showHalfSlides === 'true';
    this.fallbackProductIds = this.dataset.fallbackProductIds ? this.dataset.fallbackProductIds.split(',') : [];
    this.dataset.breakpointOverride = this.dataset.breakpointOverride || 'false';
    this.swiper = null;
    
    // Hide recently viewed and recommendations on search pages with results
    if (this.isRecentlyViewed || this.isRecommendations) {
      const isSearchPage = window.location.pathname === '/search' || window.location.pathname.includes('/search');
      const searchResultsElement = document.querySelector('[data-search-count]');
      const hasResults = searchResultsElement && parseInt(searchResultsElement.getAttribute('data-search-count') || '0') > 0;
      
      if (isSearchPage && hasResults) {
        this.parentSection ? this.parentSection.style.display = 'none' : this.style.display = 'none'
        return;
      }
    }
  }

  async connectedCallback() {
    // Initialize Swiper first so skeleton cards display in carousel
    this.initSwiper();
    
    // Add cart open event listener to re-initialize Swiper
    this.#setupCartOpenListener();
    
    if (this.isRecentlyViewed) {
      await this.loadRecentlyViewedProducts();
      // Update Swiper after products are loaded
      if (this.swiper) {
        this.swiper.update();
      }
    } else if (this.isRecommendations) {
      await this.loadRecommendedProducts();
      // Update Swiper after products are loaded
      if (this.swiper) {
        this.swiper.update();
      }
    }
  }

  initSwiper() {
    if (this.swiperEl) {
      // @ts-ignore - Swiper is loaded globally via script tag
      this.swiper = new (window.Swiper || Swiper)(this.swiperEl, {
        // Add your Swiper options here
        loop: false,
        slidesPerView: 1.5,
        spaceBetween: 20,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: this.nextArrow,
          prevEl: this.prevArrow,
          enabled: false,
        },
        scrollbar: {
        el: `.swiper-scrollbar`,
          draggable: true,
          hide: false,
        },
        breakpoints: this.dataset.breakpointOverride === 'true' ? {
          0: {
            slidesPerView: 1.6,
            snapOnRelease: true,
            spaceBetween: 10,
            navigation: {
              enabled: false
            },
          }
        } : {
          640: {
            slidesPerView: this.showHalfSlides ? 2.5 : 2,
          },
          1024: {
            slidesPerView: this.showHalfSlides ? 4.5 : 4,
            navigation: {
              enabled: true,
            },
          },
          1280: {
            slidesPerView: this.showHalfSlides ? 5.5 : 5,
            navigation: {
              enabled: true,
            },
          }
        },
        on: {
          init: () => {
            // Remove initializing class and show carousel smoothly
            if (this.swiperEl) {
              this.swiperEl.classList.remove('swiper-initializing');
              this.swiperEl.classList.add('swiper-initialized');
            }
          }
        }
      });
    }
  }


  #setupCartOpenListener() {
    // Listen for cart ADD and UPDATE events (but filter out updates during item deletion)
    const cartEvents = [
      'CartAddEvent',
      'cart:add',
      'cart:update',
      'ThemeEvents.cartUpdate',
      'CartUpdateEvent',
      'cart:change',
      'theme:cart:update'
    ];
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let resetTimer;
    const resetSwiper = () => {
      //only reset carousels in cart drawer
      const isInCart = this.hasAttribute('data-is-cart-drawer');
      if (!isInCart) {
        return;
      }

      // Debounce: coalesce multiple rapid cart events (e.g. Rise AI's
      // multi-step add flow) into a single swiper reset
      if (resetTimer) {
        clearTimeout(resetTimer);
      }
      
      resetTimer = setTimeout(() => {
        resetTimer = undefined;
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
        
        // Re-query the swiper element since the cart DOM may have been
        // replaced by component-cart-items during the debounce window
        this.swiperEl = this.querySelector('.swiper');
        if (this.swiperEl) {
          this.swiperEl.className = 'swiper';
        }
        this.nextArrow = this.querySelector('.swiper-button-next');
        this.prevArrow = this.querySelector('.swiper-button-prev');

        // Re-initialize from scratch
        this.initSwiper();
      }, 500);
    };
    
    cartEvents.forEach(eventName => {
      document.addEventListener(eventName, resetSwiper);
    });
    
    // Listen for cart drawer dialog open events
    const cartDrawer = document.querySelector('cart-drawer-component dialog.cart-drawer__dialog');
    
    if (cartDrawer) {
      // Also check for dialog open state
      const dialogObserver = new MutationObserver(() => {
        const isOpen = cartDrawer.hasAttribute('open');
        
        if (isOpen) {
          resetSwiper();
        }
      });
      
      dialogObserver.observe(cartDrawer, { 
        attributes: true, 
        childList: true, 
        subtree: false 
      });
    }
  }

  async loadRecentlyViewedProducts() {
    const productIds = RecentlyViewed.getProducts();
    
    // No recently viewed products, hide the section
    if (productIds.length === 0) {
      this.parentSection ? this.parentSection.style.display = 'none' : this.style.display = 'none'
      return;
    }

    const targetWrapper = this.querySelector('[data-recently-viewed-products]');
    if (!targetWrapper) {
      console.error('Product wrapper not found');
      return;
    }
    
    try {
      // Build search query: id:123 OR id:456  
      const searchQuery = productIds.map(id => `id:${id}`).join(' OR ');
      
      // Use THIS carousel section with search query - it will render with custom-product-tile
      const url = new URL('/search', window.location.origin);
      url.searchParams.set('q', searchQuery);
      url.searchParams.set('type', 'product');
      
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
      
      // Parse the returned HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find swiper slides rendered by the section
      const productSlides = doc.querySelectorAll('.swiper-slide');
      
      if (productSlides.length === 0) {
        this.parentSection ? this.parentSection.style.display = 'none' : this.style.display = 'none'
        return;
      }

      // Sort products to match localStorage order
      const sortedSlides = Array.from(productSlides).sort((a, b) => {
        const aId = a.querySelector('[data-product-id]')?.getAttribute('data-product-id') || '';
        const bId = b.querySelector('[data-product-id]')?.getAttribute('data-product-id') || '';
        const aIndex = productIds.indexOf(aId);
        const bIndex = productIds.indexOf(bId);
        
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        
        return aIndex - bIndex;
      });

      // Insert sorted slides with carousel styling
      targetWrapper.innerHTML = sortedSlides.map(slide => slide.outerHTML).join('');
      
      // Remove initializing class from swiper
      const swiperElement = this.querySelector('.swiper');
      if (swiperElement) {
        swiperElement.classList.remove('swiper-initializing');
      }
      
    } catch (error) {
      console.error('Failed to load recently viewed products:', error);
      this.parentSection ? this.parentSection.style.display = 'none' : this.style.display = 'none'
    }
  }

  async loadRecommendedProducts() {
    // Use current product ID if provided, otherwise get the most recently viewed product
    let productIdForRecommendations = this.currentProductId;
    
    if (!productIdForRecommendations) {
      const productIds = RecentlyViewed.getProducts();
      
      if (productIds.length === 0) {
        // No recently viewed products, try fallback collection
        await this.loadFallbackCollection();
        return;
      }
      
      productIdForRecommendations = productIds[0];
    }

    const targetWrapper = this.querySelector('[data-recommendations-products]');
    if (!targetWrapper) return;

    try {
      if (!productIdForRecommendations) {
        await this.loadFallbackCollection();
        return;
      }
      
      // Use Shopify's recommendations JSON endpoint to get product IDs
      const recommendationsUrl = new URL('/recommendations/products.json', window.location.origin);
      recommendationsUrl.searchParams.set('product_id', productIdForRecommendations);
      recommendationsUrl.searchParams.set('limit', '10');
      
      const recResponse = await fetch(recommendationsUrl);
      const data = await recResponse.json();
      const products = data.products || [];
      
      if (products.length === 0) {
        // No recommendations, try fallback collection
        await this.loadFallbackCollection();
        return;
      }

      // Build search query with recommended product IDs
      // @ts-ignore - products from recommendations API
      const searchQuery = products.map(p => `id:${p.id}`).join(' OR ');
      
      // Use THIS carousel section with search query - it will render with custom-product-tile
      const url = new URL('/search', window.location.origin);
      url.searchParams.set('q', searchQuery);
      url.searchParams.set('type', 'product');
      
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
      
      // Parse the returned HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find swiper slides rendered by the section
      const productSlides = doc.querySelectorAll('.swiper-slide');
      
      if (productSlides.length === 0) {
        // No products found, try fallback collection
        await this.loadFallbackCollection();
        return;
      }

      // Insert slides (already in correct order from recommendations API)
      targetWrapper.innerHTML = Array.from(productSlides).map(slide => slide.outerHTML).join('');
      
      // Remove initializing class from swiper
      const swiperElement = this.querySelector('.swiper');
      if (swiperElement) {
        swiperElement.classList.remove('swiper-initializing');
      }
      
      // Update Swiper
      if (this.swiper) {
        this.swiper.update();
      }
      
    } catch (error) {
      console.error('Failed to load recommended products:', error);
      await this.loadFallbackCollection();
    }
  }

  async loadFallbackCollection() {
    // If no fallback collection is configured, hide the carousel
    if (this.fallbackProductIds.length === 0) {
      this.parentSection ? this.parentSection.style.display = 'none' : this.style.display = 'none'
      return;
    }

    const targetWrapper = this.querySelector('[data-recommendations-products]');
    if (!targetWrapper) {
      this.parentSection ? this.parentSection.style.display = 'none' : this.style.display = 'none'
      return;
    }

    try {
      // Build search query with fallback product IDs
      const searchQuery = this.fallbackProductIds.map(id => `id:${id}`).join(' OR ');
      
      // Use THIS carousel section with search query
      const url = new URL('/search', window.location.origin);
      url.searchParams.set('q', searchQuery);
      url.searchParams.set('type', 'product');
      
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
      
      // Parse the returned HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find swiper slides rendered by the section
      const productSlides = doc.querySelectorAll('.swiper-slide');
      
      if (productSlides.length === 0) {
        this.parentSection ? this.parentSection.style.display = 'none' : this.style.display = 'none'
        return;
      }

      // Insert slides
      targetWrapper.innerHTML = Array.from(productSlides).map(slide => slide.outerHTML).join('');
      
      // Remove initializing class from swiper
      const swiperElement = this.querySelector('.swiper');
      if (swiperElement) {
        swiperElement.classList.remove('swiper-initializing');
      }
      
      // Update Swiper
      if (this.swiper) {
        this.swiper.update();
      }
      
    } catch (error) {
      console.error('Failed to load fallback collection:', error);
      this.parentSection ? this.parentSection.style.display = 'none' : this.style.display = 'none'
    }
  }

}

customElements.define('product-carousel', ProductCarousel);
